import { motion } from "framer-motion";

const chapters = [
  {
    n: "01",
    title: "The Art Lounge Nirvana",
    body: "A distinguished cultural destination where contemporary creativity meets India\u2019s timeless artistic heritage. Founded by artist, curator and heritage specialist Aparna Banerjee, the studio-gallery has evolved into a vibrant platform for exhibitions, art education, bespoke heritage journeys, corporate cultural engagement and thoughtfully curated interiors.",
  },
  {
    n: "02",
    title: "Artist. Curator. Heritage Specialist.",
    body: "Aparna is a contemporary artist and cultural entrepreneur whose work bridges classical Indian traditions with contemporary artistic expression. Having studied multiple traditional Indian art forms alongside architecture, museum studies and cultural history, she brings a rare interdisciplinary perspective to every project.",
  },
  {
    n: "03",
    title: "A living ecosystem",
    body: "Through The Art Lounge Nirvana she has built a thriving ecosystem that nurtures artists, educates young minds, curates heritage experiences and collaborates with institutions, corporations and collectors across India.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Manifesto() {
  return (
    <section
      id="artist"
      className="relative py-24 md:py-36 bg-paper"
      data-testid="artist-section"
    >
      <div className="max-w-screen-2xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 lg:gap-20">
        {/* Sticky heading + portrait piece */}
        <div className="md:col-span-5">
          <div className="md:sticky md:top-28">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="font-body text-[11px] tracking-[0.35em] uppercase text-ochre mb-5"
            >
              The Founder &mdash; Aparna Banerjee
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-ink tracking-tight"
            >
              Where art,
              <br />
              <span className="italic text-ochre">heritage</span> &amp; culture
              <br />
              come alive.
            </motion.h2>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="mt-10 relative w-full max-w-xs overflow-hidden gallery-shadow border-[6px] border-paper bg-sand aspect-[4/5]"
              data-cursor="Roop Chaudas"
            >
              <img
                src="/artworks/premium/roop-chaudas.jpeg"
                alt="Roop Chaudas by Aparna Banerjee"
                className="h-full w-full object-cover"
              />
            </motion.div>
          </div>
        </div>

        {/* Numbered chapters */}
        <div className="md:col-span-7 flex flex-col">
          {chapters.map((c, i) => (
            <motion.article
              key={c.n}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              className="border-t border-ink-soft/20 py-10 md:py-14 grid grid-cols-[auto,1fr] gap-6 md:gap-10"
            >
              <span className="font-serif text-3xl md:text-5xl text-ochre/70 leading-none">
                {c.n}
              </span>
              <div>
                <h3 className="font-serif text-2xl md:text-4xl text-ink leading-tight tracking-tight">
                  {c.title}
                </h3>
                <p className="mt-4 font-body text-base md:text-lg text-ink-soft leading-relaxed max-w-xl">
                  {c.body}
                </p>
              </div>
            </motion.article>
          ))}

          <div className="border-t border-ink-soft/20 pt-10 flex flex-wrap gap-x-12 gap-y-6">
            {[
              ["100+", "Exhibitions worldwide"],
              ["25+", "Years of practice"],
              ["12", "Art forms mastered"],
            ].map(([k, v]) => (
              <div key={k}>
                <p className="font-serif text-4xl text-ink">{k}</p>
                <p className="font-body text-xs tracking-[0.15em] uppercase text-ink-soft mt-1">
                  {v}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
